import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Col,
  Row,
  Collapse,
} from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faInfoCircle, faEye, faTrash, faLock, faUnlock, faUniversity } from "@fortawesome/free-solid-svg-icons";
import "./BankDetails.css";
import IbanForm from "./PaymentType/IbanForm";
import SwiftForm from "./PaymentType/SwiftForm";
import AccountNoRegNoForm from "./PaymentType/AccountNoRegNoForm";
import AbaRoutingNumberForm from "./PaymentType/AbaRoutingNumberForm";
import BankSearch from "./BankSearch";
import { paymentTypes, displayBankType } from "../../../../utils/constants";
import Select from "react-select";

/* ── Reusable Section Card ── */
const SectionCard = ({ icon, title, subtitle, children }) => (
  <div className="emp-section-card">
    <div className="emp-section-card__header">
      <div className="emp-section-card__title">
        <FontAwesomeIcon icon={icon} className="emp-section-card__icon" />
        <div>
          <span>{title}</span>
          {subtitle && <small className="emp-section-card__subtitle">{subtitle}</small>}
        </div>
      </div>
    </div>
    <div className="emp-section-card__body">{children}</div>
  </div>
);

const BankDetails = ({
  employeeLoaded,
  handleSubmit,
  onSubmit,
  alerts,
  setAlerts,
  purpose,
  setPurpose,
  purposeOption,
  register,
  paymentType,
  setPaymentType,
  errors,
  setError,
  clearErrors,
  setValue,
  getValues,
  watch,
  enableSubmit,
  setEnableSubmit,
  isBankAccountNumberValid,
  setIsBankAccountNumberValid,
  isBICValid,
  setIsBICValid,
  isCorrespondingBICValid,
  setIsCorrespondingBICValid,
  token,
  onGetIBAN,
  resetMetadata,
  handleAccountNo,
  showRegNo,
  beneficiaryNameIban,
  onBeneficiaryNameChangeIban,
  isIbanValidated,
  countryCode,
  setCountryCode,
  availableCurrencyList,
  sortedCurrencyList,
  currency,
  onCurrencyChanged,
  keepThisOnly,
  setKeepThisOnly,
  regNo,
  setRegNo,
  isRegNoValid,
  setIsRegNoValid,
  bankAccountNumber,
  accountNumberLength,
  isFinanceAccount,
  beneficiaryNameSwift,
  onBeneficiaryNameChangeSwift,
  handleOnChangeRegistrationNo,
  onGetBIC,
  isBicValidated,
  isABARoutningNumberValidated,
  onGetABARoutingNumber,
  isRegnoAccountValidated,
  onValidateRegnoAccount,
  accountType,
  setAccountType,
  accountTypeOptions,
  bankDataList,
  viewBankData,
  onDeleteBankData,
  countryList,
}) => {
  const [selectedBank, setSelectedBank] = useState(null);
  const [allowEdit, setAllowEdit] = useState(false);

  // Watch BIC field for controlled component
  const bicValue = watch ? watch("bic") : "";
  const correspondingBicValue = watch ? watch("correspondingBic") : "";
  const abaRoutingNumberValue = watch ? watch("abaRoutingNumber") : "";



  // Auto-fill fields when both bank and payment type are selected
  useEffect(() => {
    if (selectedBank && paymentType) {
      autofillBankFields(selectedBank, paymentType);
      setAllowEdit(false); // Reset allowEdit when autofilling
    }
  }, [selectedBank, paymentType]);

  useEffect(() => {
    if (getValues) {
      if (paymentType === paymentTypes.SWIFT) {
        const bic = getValues("bic");
        const accountNumber = getValues("bankAccountNumber");
        const beneficiaryName = getValues("beneficiaryBankAccountName");
        const currencyId = getValues("currencyId");
        const purposeValue = purpose;

        // Check if all required SWIFT fields are filled
        // Enable if: (bank selected from MyHR and fields filled) OR (manually validated and fields filled)
        if (bic && accountNumber && beneficiaryName && currencyId && purposeValue) {
          if (selectedBank !== null || isBicValidated) {
            setEnableSubmit(true);
          }
        } else {
          setEnableSubmit(false);
        }
      } else if (paymentType === paymentTypes.IBAN) {
        const ibanAccountNumber = getValues("bankAccountNumber");
        const beneficiaryName = getValues("beneficiaryBankAccountName");
        const currencyId = getValues("currencyId");
        const purposeValue = purpose;

        // Check if all required IBAN fields are filled
        // Enable if: (bank selected from MyHR and fields filled) OR (manually validated and fields filled)
        if (ibanAccountNumber && beneficiaryName && currencyId && purposeValue) {
          if (selectedBank !== null || isIbanValidated) {
            setEnableSubmit(true);
          }
        } else {
          setEnableSubmit(false);
        }
      } else if (paymentType === paymentTypes.ABARoutingNumber) {
        const abaRoutingNum = getValues("abaRoutingNumber");
        const accountNumber = getValues("bankAccountNumber");
        const beneficiaryName = getValues("beneficiaryBankAccountName");
        const currencyId = getValues("currencyId");
        const purposeValue = purpose;
        const accountTypeValue = accountType;

        // Check if all required ABA fields are filled
        // Enable if: (bank selected from MyHR and fields filled) OR (manually validated and fields filled)
        if (abaRoutingNum && accountNumber && beneficiaryName && currencyId && purposeValue && accountTypeValue) {
          if (selectedBank !== null || isABARoutningNumberValidated) {
            setEnableSubmit(true);
          }
        } else {
          setEnableSubmit(false);
        }
      } else if (paymentType === paymentTypes.REGNOACCOUNT) {
        const regNoValue = getValues("regNo");
        const accountNumber = getValues("bankAccountNumber");
        const purposeValue = purpose;

        // Check if all required REGNO fields are filled
        // Enable if: (bank selected from MyHR and fields filled) OR (manually validated and fields filled)
        if (regNoValue && accountNumber && purposeValue) {
          if (selectedBank !== null || isRegnoAccountValidated) {
            setEnableSubmit(true);
          }
        } else {
          setEnableSubmit(false);
        }
      }
    }
  }, [paymentType, bicValue, bankAccountNumber, beneficiaryNameSwift, beneficiaryNameIban, purpose, accountType, abaRoutingNumberValue, regNo, selectedBank, isBicValidated, isIbanValidated, isABARoutningNumberValidated, isRegnoAccountValidated]);

  const autofillBankFields = (bank, type) => {
    // Use setTimeout to ensure state updates properly
    setTimeout(() => {
      switch(type) {
        case paymentTypes.SWIFT:
          setValue("abaRoutingNumber", "", { shouldValidate: false });
          setValue("state", "", { shouldValidate: false });
          
          if (bank.businessIdentifierCode) {
            setValue("bic", bank.businessIdentifierCode, { shouldValidate: false, shouldDirty: true, shouldTouch: true });
          }
        if (bank.bankName) {
          setValue("bankName", bank.bankName, { shouldValidate: true, shouldDirty: true });
        }
        if (bank.bankBranch) {
          setValue("branch", bank.bankBranch, { shouldValidate: true, shouldDirty: true });
        }
        setValue("bankAddress", bank.street || "", { shouldValidate: true, shouldDirty: true });
        setValue("bankCity", bank.city || "", { shouldValidate: true, shouldDirty: true });
        setValue("bankZip", bank.postalCode || "", { shouldValidate: true, shouldDirty: true });
        if (bank.bankCountry) {
          setValue("bankCountry", bank.bankCountry, { shouldValidate: true, shouldDirty: true });
          setCountryCode(bank.bankCountry);
        }
        // Enable submit will be handled by useEffect checking all required fields
        break;
        
      case paymentTypes.IBAN:
        setValue("abaRoutingNumber", "", { shouldValidate: false });
        setValue("state", "", { shouldValidate: false });
        setValue("correspondingBic", "", { shouldValidate: false });
        
        // Auto-fill IBAN specific fields
        if (bank.businessIdentifierCode) {
          setValue("bic", bank.businessIdentifierCode, { shouldValidate: false, shouldDirty: true });
        }
        if (bank.bankName) {
          setValue("bankName", bank.bankName, { shouldValidate: false, shouldDirty: true });
        }
        if (bank.bankBranch) {
          setValue("branch", bank.bankBranch, { shouldValidate: false, shouldDirty: true });
        }
        // Use empty fallback when MyHR doesn't provide a value
        setValue("bankCity", bank.city || "", { shouldValidate: false, shouldDirty: true });
        setValue("bankZip", bank.postalCode || "", { shouldValidate: false, shouldDirty: true });
        if (bank.bankCountry) {
          setValue("bankCountry", bank.bankCountry, { shouldValidate: false, shouldDirty: true });
          setCountryCode(bank.bankCountry);
        }
        // Enable submit will be handled by useEffect checking all required fields
        break;
        
      case paymentTypes.ABARoutingNumber:
        setValue("bic", "", { shouldValidate: false });
        setValue("correspondingBic", "", { shouldValidate: false });
        setValue("regNo", "", { shouldValidate: false });
        
        if (bank.routingNumber) {
          setValue("abaRoutingNumber", bank.routingNumber, { shouldValidate: false, shouldDirty: true });
        }
        if (bank.bankName) {
          setValue("bankName", bank.bankName, { shouldValidate: false, shouldDirty: true });
        }
        // Use empty fallback when MyHR doesn't provide a value
        setValue("bankCity", bank.city || "", { shouldValidate: false, shouldDirty: true });
        setValue("bankZip", bank.postalCode || "", { shouldValidate: false, shouldDirty: true });
        if (bank.bankCountry) {
          setValue("state", bank.bankCountry, { shouldValidate: false, shouldDirty: true });
          setValue("bankCountry", bank.bankCountry, { shouldValidate: false, shouldDirty: true });
        }
        // ABA-specific fields — leave blank when not provided
        setValue("county", "", { shouldValidate: false, shouldDirty: true });
        setValue("timezone", "", { shouldValidate: false, shouldDirty: true });
        // Enable submit will be handled by useEffect checking all required fields
        break;
        
      case paymentTypes.REGNOACCOUNT:
        setValue("bic", "", { shouldValidate: false });
        setValue("correspondingBic", "", { shouldValidate: false });
        setValue("abaRoutingNumber", "", { shouldValidate: false });
        
        if (bank.routingNumber) {
          setValue("regNo", bank.routingNumber, { shouldValidate: false, shouldDirty: true });
          setRegNo(bank.routingNumber);
        }
        // Auto-fill REGNO specific fields
        if (bank.bankName) {
          setValue("bankName", bank.bankName, { shouldValidate: false, shouldDirty: true });
        }
        setValue("bankAddress", bank.street || "", { shouldValidate: false, shouldDirty: true });
        setValue("bankCity", bank.city || "", { shouldValidate: false, shouldDirty: true });
        setValue("bankZip", bank.postalCode || "", { shouldValidate: false, shouldDirty: true });
        if (bank.bankCountry) {
          setValue("bankCountry", bank.bankCountry, { shouldValidate: false, shouldDirty: true });
        }
        setValue("iban", bank.iban || "", { shouldValidate: false, shouldDirty: true });
        // Enable submit will be handled by useEffect checking all required fields
        break;
      }
    }, 0);
  };

  const clearBankFields = () => {
    // Clear all possible bank fields to avoid mixing data between payment types
    setValue("bic", "", { shouldValidate: false });
    setValue("correspondingBic", "", { shouldValidate: false });
    setValue("bankName", "", { shouldValidate: false });
    setValue("branch", "", { shouldValidate: false });
    setValue("bankAddress", "", { shouldValidate: false });
    setValue("bankCity", "", { shouldValidate: false });
    setValue("bankZip", "", { shouldValidate: false });
    setValue("bankCountry", "", { shouldValidate: false });
    setCountryCode("");
    setValue("abaRoutingNumber", "", { shouldValidate: false });
    setValue("state", "", { shouldValidate: false });
    setValue("regNo", "", { shouldValidate: false });
    setRegNo("");
  };

  const handleBankSelect = (bank) => {
    setSelectedBank(bank);
    
    // If clearing bank selection (bank is null), reset validation states and clear auto-filled fields
    if (bank === null) {
      setEnableSubmit(false);
      
      if (paymentType === paymentTypes.SWIFT) {
        // Clear auto-filled SWIFT fields
        setValue("bic", "", { shouldValidate: false });
        setValue("correspondingBic", "", { shouldValidate: false });
        setValue("bankName", "", { shouldValidate: false });
        setValue("branch", "", { shouldValidate: false });
        setValue("bankAddress", "", { shouldValidate: false });
        setValue("bankCity", "", { shouldValidate: false });
        setValue("bankZip", "", { shouldValidate: false });
        setValue("bankCountry", "", { shouldValidate: false });
        setCountryCode("");
      } else if (paymentType === paymentTypes.IBAN) {
        // Clear auto-filled IBAN fields
        setValue("bic", "", { shouldValidate: false });
        setValue("bankAccountNumber", "", { shouldValidate: false });
        setValue("bankName", "", { shouldValidate: false });
        setValue("branch", "", { shouldValidate: false });
        setValue("bankCity", "", { shouldValidate: false });
        setValue("bankZip", "", { shouldValidate: false });
        setValue("bankCountry", "", { shouldValidate: false });
        setCountryCode("");
      } else if (paymentType === paymentTypes.ABARoutingNumber) {
        // Clear auto-filled ABA fields
        setValue("abaRoutingNumber", "", { shouldValidate: false });
        setValue("bankName", "", { shouldValidate: false });
        setValue("bankCity", "", { shouldValidate: false });
        setValue("state", "", { shouldValidate: false });
        setValue("county", "", { shouldValidate: false });
        setValue("bankZip", "", { shouldValidate: false });
        setValue("bankCountry", "", { shouldValidate: false });
        setValue("timezone", "", { shouldValidate: false });
      } else if (paymentType === paymentTypes.REGNOACCOUNT) {
        // Clear auto-filled REGNO fields
        setValue("regNo", "", { shouldValidate: false });
        setRegNo("");
        setValue("iban", "", { shouldValidate: false });
        setValue("bankName", "", { shouldValidate: false });
        setValue("bankAddress", "", { shouldValidate: false });
        setValue("bankCity", "", { shouldValidate: false });
        setValue("bankZip", "", { shouldValidate: false });
        setValue("bankCountry", "", { shouldValidate: false });
      }
    }
  };

  return (
    <Row className={"mb-3"}>
      {employeeLoaded && (
        <Col sm={12}>
          <SectionCard
            icon={faUniversity}
            title="Bank Details"
            subtitle="Please ensure that there is only one currency for each bank account number. All fields marked with * are required."
          >
            <Form
              onSubmit={handleSubmit(onSubmit, (errors) => {
                
              })}
            >

                {/* Option A: Find existing bank in MyHR */}
                <div className="bank-step">
                  <div className="bank-step__header">
                    <span className="bank-step__badge">Option A</span>
                    <div>
                      <div className="bank-step__title">Search for your bank</div>
                      <div className="bank-step__subtitle">
                        Search MyHR for an existing bank to auto-fill its details.
                        If your bank isn't listed, skip this and enter the details manually in Option B below.
                      </div>
                    </div>
                  </div>
                  <BankSearch
                    token={token}
                    onBankSelect={handleBankSelect}
                    selectedBank={selectedBank}
                    countryList={countryList}
                  />
                </div>

                {/* Option B: Enter the account details */}
                <div className="bank-step">
                  <div className="bank-step__header">
                    <span className="bank-step__badge">Option B</span>
                    <div>
                      <div className="bank-step__title">Enter your account details</div>
                      <div className="bank-step__subtitle">
                        Choose how you want to identify the account, then fill in the required fields.
                      </div>
                    </div>
                  </div>
                  <div>
                  <FormGroup tag="fieldset" className="bank-payment-types">
                    <legend className="bank-payment-types__legend">Bank identifier</legend>
                    <div className="bs-tabs bank-payment-types__tabs">
                      {[
                        { value: paymentTypes.IBAN, label: "IBAN" },
                        { value: paymentTypes.SWIFT, label: "SWIFT" },
                        { value: paymentTypes.REGNOACCOUNT, label: "REGNO/ACCOUNT (DK)" },
                        { value: paymentTypes.ABARoutingNumber, label: "ABA Routing Number" },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          className={`bs-tab${paymentType === option.value ? " bs-tab--active" : ""}`}
                          onClick={() => {
                            const next = paymentType === option.value ? null : option.value;
                            setPaymentType(next);
                            setValue("type", next ?? "", { shouldValidate: false });
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </FormGroup>

                  {/* Conditional rendering for payment type forms */}
                  {paymentType && (
                    <div className="bank-fields">
                  {paymentType === paymentTypes.IBAN && (
                    <IbanForm
                      register={register}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      bic={bicValue}
                      enableSubmit={enableSubmit}
                      setEnableSubmit={setEnableSubmit}
                      isBankAccountNumberValid={isBankAccountNumberValid}
                      setIsBankAccountNumberValid={
                        setIsBankAccountNumberValid
                      }
                      isBICValid={isBICValid}
                      setIsBICValid={setIsBICValid}
                      isCorrespondingBICValid={isCorrespondingBICValid}
                      setIsCorrespondingBICValid={setIsCorrespondingBICValid}
                      token={token}
                      checkbox={paymentType}
                      onGetIBAN={onGetIBAN}
                      resetMetadata={resetMetadata}
                      handleAccountNo={handleAccountNo}
                      showRegNo={showRegNo}
                      beneficiaryName={beneficiaryNameIban}
                      onBeneficiaryNameChange={onBeneficiaryNameChangeIban}
                      isIbanValidated={isIbanValidated}
                      isoCountryCode={countryCode}
                      onCountryCodeChange={(val) => setCountryCode(val)}
                      selectedBankFromMyHR={selectedBank !== null}
                      currencyList={availableCurrencyList}
                      sortedCurrencyList={sortedCurrencyList}
                      currency={currency}
                      onCurrencyChanged={onCurrencyChanged}
                      keepThisOnly={keepThisOnly}
                      onKeepThisOnlyChange={() => setKeepThisOnly(!keepThisOnly)}
                      purpose={purpose}
                      setPurpose={setPurpose}
                      purposeOption={purposeOption}
                      allowEdit={allowEdit}
                      setAllowEdit={setAllowEdit}
                    />
                  )}

                  {paymentType === paymentTypes.SWIFT && (
                    <SwiftForm
                      register={register}
                      errors={errors}
                      setError={setError}
                      clearErrors={clearErrors}
                      setValue={setValue}
                      bic={bicValue}
                      correspondingBic={correspondingBicValue}
                      enableSubmit={enableSubmit}
                      setEnableSubmit={setEnableSubmit}
                      isBankAccountNumberValid={isBankAccountNumberValid}
                      setIsBankAccountNumberValid={
                        setIsBankAccountNumberValid
                      }
                      isBICValid={isBICValid}
                      setIsBICValid={setIsBICValid}
                      isCorrespondingBICValid={isCorrespondingBICValid}
                      setIsCorrespondingBICValid={setIsCorrespondingBICValid}
                      token={token}
                      showRegNo={showRegNo}
                      regNo={regNo}
                      setRegNo={setRegNo}
                      isRegNoValid={isRegNoValid}
                      setIsRegNoValid={setIsRegNoValid}
                      bankAccountNumber={bankAccountNumber}
                      accountNumberLength={accountNumberLength}
                      isFinanceAccount={isFinanceAccount}
                      beneficiaryName={beneficiaryNameSwift}
                      onBeneficiaryNameChange={onBeneficiaryNameChangeSwift}
                      handleAccountNo={handleAccountNo}
                      handleOnChangeRegistrationNo={
                        handleOnChangeRegistrationNo
                      }
                      onGetBIC={onGetBIC}
                      isBicValidated={isBicValidated}
                      isoCountryCode={countryCode}
                      onCountryCodeChange={(val) => setCountryCode(val)}
                      selectedBankFromMyHR={selectedBank !== null}
                      currencyList={availableCurrencyList}
                      sortedCurrencyList={sortedCurrencyList}
                      currency={currency}
                      onCurrencyChanged={onCurrencyChanged}
                      keepThisOnly={keepThisOnly}
                      onKeepThisOnlyChange={() => setKeepThisOnly(!keepThisOnly)}
                      purpose={purpose}
                      setPurpose={setPurpose}
                      purposeOption={purposeOption}
                      allowEdit={allowEdit}
                      setAllowEdit={setAllowEdit}
                    />
                  )}

                  {paymentType === paymentTypes.REGNOACCOUNT && (
                    <AccountNoRegNoForm
                      register={register}
                      errors={errors}
                      checkbox={paymentType}
                      regNo={regNo}
                      setRegNo={setRegNo}
                      isRegNoValid={isRegNoValid}
                      setIsRegNoValid={setIsRegNoValid}
                      handleOnChangeRegistrationNo={handleOnChangeRegistrationNo}
                      bankAccountNumber={bankAccountNumber}
                      handleAccountNo={handleAccountNo}
                      isBankAccountNumberValid={isBankAccountNumberValid}
                      setIsBankAccountNumberValid={
                        setIsBankAccountNumberValid
                      }
                      isRegnoAccountValidated={isRegnoAccountValidated}
                      selectedBankFromMyHR={selectedBank !== null}
                      purpose={purpose}
                      setPurpose={setPurpose}
                      purposeOption={purposeOption}
                      allowEdit={allowEdit}
                      setAllowEdit={setAllowEdit}
                    />
                  )}

                  {paymentType === paymentTypes.ABARoutingNumber && (
                    <AbaRoutingNumberForm
                      register={register}
                      errors={errors}
                      setValue={setValue}
                      clearErrors={clearErrors}
                      resetMetadata={resetMetadata}
                      abaRoutingNumber={abaRoutingNumberValue}
                      isAbaRoutingNumberValidated={isABARoutningNumberValidated}
                      selectedBankFromMyHR={selectedBank !== null}
                      currencyList={availableCurrencyList}
                      sortedCurrencyList={sortedCurrencyList}
                      currency={currency}
                      onCurrencyChanged={onCurrencyChanged}
                      accountType={accountType}
                      setAccountType={setAccountType}
                      accountTypeOptions={accountTypeOptions}
                      purpose={purpose}
                      setPurpose={setPurpose}
                      purposeOption={purposeOption}
                      allowEdit={allowEdit}
                      setAllowEdit={setAllowEdit}
                    />
                  )}
                    <div className="bank-fields__actions">
                      {/* Validate button for IBAN - only shown if no bank selected from MyHR search */}
                      {paymentType === paymentTypes.IBAN &&
                        !selectedBank &&
                        !isIbanValidated && (
                          <Button
                            className="btn-hafnia"
                            type="button"
                            onClick={onGetIBAN}
                          >
                            Validate IBAN
                          </Button>
                        )}

                      {/* Validate button for SWIFT - only shown if no bank selected from MyHR search */}
                      {paymentType === paymentTypes.SWIFT &&
                        !selectedBank &&
                        !isBicValidated && (
                          <Button
                            className="btn-hafnia"
                            type="button"
                            onClick={onGetBIC}
                          >
                            Validate SWIFT BIC
                          </Button>
                        )}

                      {/* Validate button for ABA Routing Number - only shown if no bank selected from MyHR search */}
                      {paymentType === paymentTypes.ABARoutingNumber &&
                        !selectedBank &&
                        !isABARoutningNumberValidated && (
                          <Button
                            className="btn-hafnia"
                            type="button"
                            onClick={onGetABARoutingNumber}
                          >
                            Validate ABA Routing Number
                          </Button>
                        )}

                      {/* Validate button for Registration number and Account number - only shown if no bank selected from MyHR search */}
                      {paymentType === paymentTypes.REGNOACCOUNT &&
                        !selectedBank &&
                        !isRegnoAccountValidated && (
                          <Button
                            className="btn-hafnia"
                            type="button"
                            onClick={onValidateRegnoAccount}
                          >
                            Validate
                          </Button>
                        )}

                      {/* OLD VERSION - Conditional Add Bank Data button */}
                      {/* {paymentType &&
                        ((paymentType === paymentTypes.SWIFT &&
                          isBicValidated) ||
                          (paymentType === paymentTypes.IBAN &&
                            isIbanValidated) ||
                          (paymentType === paymentTypes.ABARoutingNumber &&
                            isABARoutningNumberValidated) ||
                          (paymentType === paymentTypes.REGNOACCOUNT &&
                            isRegnoAccountValidated)) && (
                          <Button
                            className="btn-hafnia"
                            type="button"
                            disabled={!enableSubmit}
                            onClick={onSubmit}
                          >
                            Add Bank Data
                          </Button>
                        )} */}

                      {/* NEW VERSION - Add Bank Data button conditional logic */}
                      {/* For ALL payment types: show if bank selected from MyHR OR if manually validated */}
                      {paymentType &&
                        ((paymentType === paymentTypes.SWIFT &&
                          (selectedBank !== null || isBicValidated)) ||
                          (paymentType === paymentTypes.IBAN &&
                            (selectedBank !== null || isIbanValidated)) ||
                          (paymentType === paymentTypes.ABARoutingNumber &&
                            (selectedBank !== null || isABARoutningNumberValidated)) ||
                          (paymentType === paymentTypes.REGNOACCOUNT &&
                            (selectedBank !== null || isRegnoAccountValidated))) && (
                          <Button
                            className="btn-hafnia"
                            type="button"
                            disabled={!enableSubmit}
                            onClick={onSubmit}
                          >
                            Add Bank Data
                          </Button>
                        )}
                    </div>
                    </div>
                  )}
                </div>
                </div>
            </Form>

            {bankDataList.length > 0 && (
              <div className="bank-list-card">
                <div className="bank-list-card__header">
                  <span className="bank-list-card__title">Added Bank Details</span>
                  <span className="bank-list-card__badge">{bankDataList.length} account{bankDataList.length > 1 ? 's' : ''}</span>
                </div>
                <div className="bank-list-card__items">
                  {bankDataList.map((bankData, index) => (
                    <div key={index} className="bank-entry">
                      <div className="bank-entry__body">
                        <div className="bank-entry__type">{bankData.bank || displayBankType(bankData.type) || "—"}</div>
                        <div className="bank-entry__meta">
                          {bankData.type && <span className="bank-entry__chip">{displayBankType(bankData.type)}</span>}
                          {bankData.currency?.currencyCode && <span className="bank-entry__chip bank-entry__chip--currency">{bankData.currency.currencyCode}</span>}
                          {bankData.purpose && <span className="bank-entry__chip bank-entry__chip--purpose">{bankData.purpose}</span>}
                        </div>
                      </div>
                      <div className="bank-entry__actions">
                        <button className="bank-entry__action-btn" onClick={() => viewBankData(bankData)} title="View details">
                          <FontAwesomeIcon icon={faEye} />
                        </button>
                        <button className="bank-entry__action-btn bank-entry__action-btn--danger" onClick={() => onDeleteBankData(bankData)} title="Delete">
                          <FontAwesomeIcon icon={faTrash} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </SectionCard>
        </Col>
      )}
    </Row>
  );
};

export default BankDetails;
